import { forwardRef, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getSkewT } from '../../services/api';
import { useForecastContext } from '../../contexts/ForecastContextProvider';
import SkewTChart from './SkewTChart';

const SkewTPanel = forwardRef(({}, ref) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    const date = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
    // Model comes from the picker (context), same as PointTableV2 — it was
    // hardcoded EUROPE here, so Skew-T ignored the ICON selection.
    const model = (useForecastContext() || {}).model || 'EUROPE';
    const rawTime = searchParams.get('time') || '1200';
    // ICON is an hourly model — a :30 timestamp 500s upstream; snap to the hour.
    const time = model === 'ICON' ? `${rawTime.slice(0, 2)}00` : rawTime;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    // Cancellable fetch — if the user scrubs the time slider, drop the previous
    // request so we don't render stale data.
    const requestId = useRef(0);

    useEffect(() => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setLoading(false);
            setData(null);
            return;
        }
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        const timeAsMs = new Date(`${date}T${time.slice(0, 2)}:${time.slice(2)}:00Z`).getTime();
        getSkewT(lat, lng, date, timeAsMs, model)
            .then((d) => {
                if (id !== requestId.current) return;
                setData(d);
                setLoading(false);
            })
            .catch((err) => {
                if (id !== requestId.current) return;
                setError(err);
                setLoading(false);
            });
    }, [lat, lng, date, time, model, retryCount]);

    // Initial load only: show the pulsing VSS logo while we have no data yet,
    // matching the PointTableV2 loader for a consistent forecast-panel feel.
    if (loading && !data) {
        return (
            <div ref={ref} data-testid="skewt-skeleton" className="h-full w-full flex items-center justify-center p-4 bg-zinc-100 dark:bg-slate-800">
                <img
                    src="/assets/images/logo.png"
                    className="h-12 sm:h-16 w-auto animate-pulse dark:invert dark:brightness-0"
                    alt="loading"
                />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div ref={ref} className="h-full w-full flex items-center justify-center flex-col gap-3 p-4">
                <p className="text-red-600 dark:text-red-400">{t("Couldn't load Skew-T")}</p>
                <button
                    type="button"
                    onClick={() => setRetryCount((c) => c + 1)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm"
                >
                    {t('Retry')}
                </button>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div ref={ref} className="h-full w-full flex items-center justify-center p-4 text-slate-600 dark:text-slate-400 text-center">
                {t('No model data for this time. Try a later timestep.')}
            </div>
        );
    }

    // On mobile (no sm:), keep a square aspect so the chart doesn't get
    // squashed when the panel is short. On larger viewports, let the chart
    // fill the available slot entirely — earlier we capped it to Skysight's
    // 948/770 ratio (1.23), but that left visible horizontal whitespace in
    // wide panels. The chart's internal scales already adapt to any aspect
    // (the data-driven tMin/tMax + piecewise skew are width/height aware).
    return (
        <div
            ref={ref}
            data-testid="skewt-chart-slot"
            className="h-full w-full flex items-center justify-center bg-white overflow-hidden relative"
        >
            <div
                data-testid="skewt-chart"
                className={`h-full w-full aspect-square sm:aspect-auto transition-opacity duration-150 ${loading ? 'opacity-60' : 'opacity-100'}`}
            >
                <SkewTChart data={data} />
            </div>
            {loading && (
                <div className="absolute top-3 right-3 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin pointer-events-none" />
            )}
        </div>
    );
});

export default SkewTPanel;
